import { pow } from '@gardener/core';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** Growable little-endian binary writer. Reuse one instance per encoder; call `reset()` between messages. */
export class ByteWriter {
  private buf: Uint8Array;
  private view: DataView;
  private pos = 0;

  constructor(initialCapacity = 256) {
    this.buf = new Uint8Array(initialCapacity);
    this.view = new DataView(this.buf.buffer);
  }

  get length(): number {
    return this.pos;
  }

  reset(): this {
    this.pos = 0;
    return this;
  }

  private ensure(extra: number): void {
    const need = this.pos + extra;
    if (need <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < need) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf.subarray(0, this.pos));
    this.buf = next;
    this.view = new DataView(next.buffer);
  }

  u8(v: number): void {
    check(v, 0, 0xff, 'u8');
    this.ensure(1);
    this.view.setUint8(this.pos, v);
    this.pos += 1;
  }

  i8(v: number): void {
    check(v, -0x80, 0x7f, 'i8');
    this.ensure(1);
    this.view.setInt8(this.pos, v);
    this.pos += 1;
  }

  u16(v: number): void {
    check(v, 0, 0xffff, 'u16');
    this.ensure(2);
    this.view.setUint16(this.pos, v, true);
    this.pos += 2;
  }

  u32(v: number): void {
    this.ensure(4);
    this.view.setUint32(this.pos, v, true);
    this.pos += 4;
  }

  f32(v: number): void {
    this.ensure(4);
    this.view.setFloat32(this.pos, v, true);
    this.pos += 4;
  }

  f64(v: number): void {
    this.ensure(8);
    this.view.setFloat64(this.pos, v, true);
    this.pos += 8;
  }

  /** Unsigned LEB128, up to 2^32-1. */
  varuint(v: number): void {
    if (v < 0 || v > 0xffffffff || !Number.isInteger(v)) throw new RangeError(`varuint out of range: ${v}`);
    this.ensure(5);
    let n = v >>> 0;
    while (n >= 0x80) {
      this.buf[this.pos++] = (n & 0x7f) | 0x80;
      n >>>= 7;
    }
    this.buf[this.pos++] = n;
  }

  /** Zig-zag signed LEB128 for 32-bit integers. */
  varint(v: number): void {
    if (!Number.isInteger(v) || v < -0x80000000 || v > 0x7fffffff) throw new RangeError(`varint out of range: ${v}`);
    this.varuint(((v << 1) ^ (v >> 31)) >>> 0);
  }

  bytes(src: Uint8Array): void {
    this.ensure(src.length);
    this.buf.set(src, this.pos);
    this.pos += src.length;
  }

  string(s: string): void {
    const encoded = textEncoder.encode(s);
    this.varuint(encoded.length);
    this.bytes(encoded);
  }

  /** Copy of the written bytes (safe to transfer or keep). */
  finish(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }
}

export class ProtocolError extends Error {}

/** Narrow integers must fit: DataView would wrap them (256 → 0) or cut fractions without a word. */
function check(v: number, min: number, max: number, what: string): void {
  if (!Number.isInteger(v) || v < min || v > max) throw new ProtocolError(`${what} out of range: ${v}`);
}

export class ByteReader {
  private readonly buf: Uint8Array;
  private readonly view: DataView;
  private pos = 0;

  constructor(buf: Uint8Array) {
    this.buf = buf;
    this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  get remaining(): number {
    return this.buf.length - this.pos;
  }

  private need(n: number): void {
    if (this.pos + n > this.buf.length) throw new ProtocolError('Unexpected end of message');
  }

  u8(): number {
    this.need(1);
    return this.view.getUint8(this.pos++);
  }

  i8(): number {
    this.need(1);
    return this.view.getInt8(this.pos++);
  }

  u16(): number {
    this.need(2);
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }

  u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }

  f32(): number {
    this.need(4);
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }

  f64(): number {
    this.need(8);
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }

  varuint(): number {
    let result = 0;
    let shift = 0;
    for (let i = 0; i < 5; i++) {
      const b = this.u8();
      result += (b & 0x7f) * pow(2, shift);
      if ((b & 0x80) === 0) return result;
      shift += 7;
    }
    throw new ProtocolError('varuint too long');
  }

  varint(): number {
    const z = this.varuint();
    return (z >>> 1) ^ -(z & 1);
  }

  bytes(n: number): Uint8Array {
    this.need(n);
    const out = this.buf.subarray(this.pos, this.pos + n);
    this.pos += n;
    return out;
  }

  rest(): Uint8Array {
    return this.bytes(this.remaining);
  }

  string(): string {
    return textDecoder.decode(this.bytes(this.varuint()));
  }
}
